<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      :aria-label="isEditing ? 'Modifier la catégorie' : 'Nouvelle catégorie budgétaire'"
      @mousedown.self="emit('close')"
    >
      <div class="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 class="text-sm font-black uppercase tracking-widest text-foreground">
            {{ isEditing ? 'Modifier la catégorie' : 'Nouvelle catégorie' }}
          </h2>
          <button
            class="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            @click="emit('close')"
            aria-label="Fermer"
          >×</button>
        </div>

        <!-- Form -->
        <form class="px-6 py-5 space-y-5" @submit.prevent="handleSubmit">

          <!-- Account type (read-only if prefilled, else selectable) -->
          <div>
            <label class="block text-xs font-black uppercase tracking-wider text-muted-foreground mb-2">Compte</label>
            <div class="flex gap-2">
              <button
                v-for="opt in accountOptions"
                :key="opt.value"
                type="button"
                class="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-xs font-bold transition-all"
                :class="form.accountType === opt.value
                  ? (opt.value === 'professional' ? 'border-indigo-500 bg-indigo-500/15 text-indigo-400' : 'border-emerald-500 bg-emerald-500/15 text-emerald-400')
                  : 'border-border text-muted-foreground hover:border-muted-foreground'"
                @click="form.accountType = opt.value"
                :disabled="!!prefilledAccountType"
              >
                <span>{{ opt.icon }}</span> {{ opt.label }}
              </button>
            </div>
          </div>

          <!-- Type: income / expense -->
          <div>
            <label class="block text-xs font-black uppercase tracking-wider text-muted-foreground mb-2">Type</label>
            <div class="flex gap-2">
              <button
                v-for="opt in typeOptions"
                :key="opt.value"
                type="button"
                class="flex-1 py-2.5 rounded-lg border text-xs font-bold transition-all"
                :class="form.type === opt.value
                  ? (opt.value === 'income' ? 'border-emerald-500 bg-emerald-500/15 text-emerald-400' : (opt.value === 'expense' ? 'border-red-500 bg-red-500/15 text-red-400' : 'border-indigo-500 bg-indigo-500/15 text-indigo-400'))
                  : 'border-border text-muted-foreground hover:border-muted-foreground'"
                @click="form.type = opt.value"
                :disabled="!!prefilledType"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>

          <!-- Name -->
          <div>
            <label for="cat-name" class="block text-xs font-black uppercase tracking-wider text-muted-foreground mb-2">
              Nom de la catégorie <span class="text-red-400">*</span>
            </label>
            <input
              id="cat-name"
              v-model="form.name"
              type="text"
              class="w-full px-3 py-2.5 rounded-lg bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary transition"
              placeholder="Ex : Montant facturé, Loyer, TJM…"
              required
              autofocus
            />
          </div>

          <!-- Formula -->
          <div>
            <div class="flex items-center justify-between mb-1.5">
              <label for="cat-formula" class="block text-xs font-black uppercase tracking-wider text-muted-foreground">
                Formule de calcul <span class="text-muted-foreground/50 font-normal normal-case tracking-normal">(optionnel)</span>
              </label>
              <div v-if="form.formula" class="flex rounded-lg bg-muted p-0.5 border border-border">
                <button
                  type="button"
                  class="px-2 py-0.5 rounded text-[10px] font-bold transition-all"
                  :class="!showReadableFormula ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'"
                  @click="showReadableFormula = false; nextTick(() => formulaInputRef?.focus())"
                >
                  Code
                </button>
                <button
                  type="button"
                  class="px-2 py-0.5 rounded text-[10px] font-bold transition-all"
                  :class="showReadableFormula ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'"
                  @click="showReadableFormula = true"
                >
                  Lisible
                </button>
              </div>
            </div>

            <div
              v-if="showReadableFormula"
              class="w-full min-h-[62px] px-3 py-2.5 rounded-lg bg-muted/50 border border-border text-sm font-sans text-foreground leading-normal whitespace-pre-wrap select-text cursor-pointer hover:border-muted-foreground/30 transition-colors"
              @click="showReadableFormula = false; nextTick(() => formulaInputRef?.focus())"
              title="Cliquer pour modifier la formule"
            >
              <span v-if="readableFormula" class="text-foreground font-medium leading-relaxed">{{ readableFormula }}</span>
              <span v-else class="text-muted-foreground/40 italic">Aucune formule définie</span>
            </div>
            <textarea
              v-else
              id="cat-formula"
              ref="formulaInputRef"
              v-model="form.formula"
              rows="2"
              class="w-full px-3 py-2.5 rounded-lg bg-muted border text-sm font-mono text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary transition resize-none"
              :class="formulaError ? 'border-red-500' : (formulaValid === true ? 'border-emerald-500' : 'border-border')"
              placeholder="{cat:12} * {cat:15}"
              @input="onFormulaInput"
            />
            <p v-if="formulaError" class="text-[10px] text-red-400 mt-1 flex items-center gap-1">
              <span>⚠</span> {{ formulaError }}
            </p>
            <p v-else-if="formulaValid === true && form.formula" class="text-[10px] text-emerald-400 mt-1 flex items-center gap-1">
              <span>✓</span> Formule valide
            </p>
            <p v-else-if="validating" class="text-[10px] text-muted-foreground mt-1">Validation en cours…</p>

            <!-- Available categories for reference (grouped & collapsible) -->
            <div class="mt-4 space-y-2">
              <button
                type="button"
                class="w-full flex items-center justify-between text-[10px] font-black uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors py-1.5 border-b border-border/60"
                @click="showReferences = !showReferences"
              >
                <span>Références de calcul disponibles :</span>
                <span class="text-[9px] transition-transform duration-200" :class="{ 'rotate-180': showReferences }">▼</span>
              </button>

              <div v-show="showReferences" class="space-y-2 pt-1">
                <!-- Help text -->
                <p class="text-[11px] text-muted-foreground leading-relaxed px-1">
                  Utilisez <code class="text-foreground/80 bg-muted px-1 py-0.5 rounded font-mono text-[10px]">{cat:ID}</code> pour référencer une autre catégorie.<br>
                  Variables : <code class="text-foreground/80 bg-muted px-1 py-0.5 rounded font-mono text-[10px]">{MOIS}</code> (1‑12),
                  <code class="text-foreground/80 bg-muted px-1 py-0.5 rounded font-mono text-[10px]">{RESTE_URSSAF:-N}</code>,
                  <code class="text-foreground/80 bg-muted px-1 py-0.5 rounded font-mono text-[10px]">{RESTE_IR:-N}</code>.<br>
                  <span class="text-muted-foreground/70">Le <code class="font-mono text-[10px]">:-N</code> permet de reculer d'une ou plusieurs années.</span>
                </p>

                <!-- Group 1: Compte Professionnel -->
                <div v-if="proCategories.length > 0" class="border border-border rounded-xl overflow-hidden bg-muted/10">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-3.5 py-2 hover:bg-muted/40 text-xs font-bold text-foreground transition-colors"
                    @click="toggleGroup('pro')"
                  >
                    <span class="flex items-center gap-1.5">
                      <span>💼</span> Compte Professionnel
                    </span>
                    <span class="text-[9px] text-muted-foreground transition-transform duration-200" :class="{ 'rotate-180': openGroups.pro }">▼</span>
                  </button>
                  <div v-show="openGroups.pro" class="p-3 bg-card border-t border-border space-y-3 max-h-56 overflow-y-auto">
                    <div v-if="proIncome.length > 0">
                      <p class="text-[9px] font-black uppercase tracking-wider text-emerald-500 mb-1.5">Recettes</p>
                      <div class="flex flex-wrap gap-1">
                        <div v-for="(cat, idx) in proIncome" :key="idx" class="flex items-center gap-0.5">
                          <button
                            type="button"
                            class="text-[10px] px-2 py-0.5 rounded bg-muted border border-border hover:border-emerald-500 hover:text-emerald-400 text-muted-foreground transition-colors text-left flex-1"
                            @click="insertCategoryRef(cat.insertText)"
                            :title="`Insérer ${cat.insertText}`"
                          >
                            <span class="font-mono text-emerald-400/80 mr-0.5">{{ cat.insertText }}</span> {{ cat.displayLabel }}
                          </button>
                          <button
                            v-if="!cat.isSelf"
                            type="button"
                            class="text-[9px] px-1.5 py-0.5 rounded bg-muted border border-border hover:border-emerald-500 hover:text-emerald-400 text-muted-foreground/60 transition-colors font-bold"
                            @click="insertCategoryRef(cat.insertTextPrev)"
                            title="Insérer la valeur du mois précédent"
                          >
                            M-1
                          </button>
                        </div>
                      </div>
                    </div>
                    <div v-if="proExpense.length > 0">
                      <p class="text-[9px] font-black uppercase tracking-wider text-red-400 mb-1.5">Dépenses</p>
                      <div class="flex flex-wrap gap-1">
                        <div v-for="(cat, idx) in proExpense" :key="idx" class="flex items-center gap-0.5">
                          <button
                            type="button"
                            class="text-[10px] px-2 py-0.5 rounded bg-muted border border-border hover:border-red-400 hover:text-red-400 text-muted-foreground transition-colors text-left flex-1"
                            @click="insertCategoryRef(cat.insertText)"
                            :title="`Insérer ${cat.insertText}`"
                          >
                            <span class="font-mono text-red-400/80 mr-0.5">{{ cat.insertText }}</span> {{ cat.displayLabel }}
                          </button>
                          <button
                            v-if="!cat.isSelf"
                            type="button"
                            class="text-[9px] px-1.5 py-0.5 rounded bg-muted border border-border hover:border-red-400 hover:text-red-400 text-muted-foreground/60 transition-colors font-bold"
                            @click="insertCategoryRef(cat.insertTextPrev)"
                            title="Insérer la valeur du mois précédent"
                          >
                            M-1
                          </button>
                        </div>
                      </div>
                    </div>
                    <div v-if="proParameter.length > 0">
                      <p class="text-[9px] font-black uppercase tracking-wider text-indigo-400 mb-1.5">Paramètres</p>
                      <div class="flex flex-wrap gap-1">
                        <div v-for="(cat, idx) in proParameter" :key="idx" class="flex items-center gap-0.5">
                          <button
                            type="button"
                            class="text-[10px] px-2 py-0.5 rounded bg-muted border border-border hover:border-indigo-400 hover:text-indigo-400 text-muted-foreground transition-colors text-left flex-1"
                            @click="insertCategoryRef(cat.insertText)"
                            :title="`Insérer ${cat.insertText}`"
                          >
                            <span class="font-mono text-indigo-400/80 mr-0.5">{{ cat.insertText }}</span> {{ cat.displayLabel }}
                          </button>
                          <button
                            v-if="!cat.isSelf"
                            type="button"
                            class="text-[9px] px-1.5 py-0.5 rounded bg-muted border border-border hover:border-indigo-400 hover:text-indigo-400 text-muted-foreground/60 transition-colors font-bold"
                            @click="insertCategoryRef(cat.insertTextPrev)"
                            title="Insérer la valeur du mois précédent"
                          >
                            M-1
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Group 2: Compte Personnel -->
                <div v-if="persoCategories.length > 0" class="border border-border rounded-xl overflow-hidden bg-muted/10">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-3.5 py-2 hover:bg-muted/40 text-xs font-bold text-foreground transition-colors"
                    @click="toggleGroup('perso')"
                  >
                    <span class="flex items-center gap-1.5">
                      <span>🏠</span> Compte Personnel
                    </span>
                    <span class="text-[9px] text-muted-foreground transition-transform duration-200" :class="{ 'rotate-180': openGroups.perso }">▼</span>
                  </button>
                  <div v-show="openGroups.perso" class="p-3 bg-card border-t border-border space-y-3 max-h-56 overflow-y-auto">
                    <div v-if="persoIncome.length > 0">
                      <p class="text-[9px] font-black uppercase tracking-wider text-emerald-500 mb-1.5">Recettes</p>
                      <div class="flex flex-wrap gap-1">
                        <div v-for="(cat, idx) in persoIncome" :key="idx" class="flex items-center gap-0.5">
                          <button
                            type="button"
                            class="text-[10px] px-2 py-0.5 rounded bg-muted border border-border hover:border-emerald-500 hover:text-emerald-400 text-muted-foreground transition-colors text-left flex-1"
                            @click="insertCategoryRef(cat.insertText)"
                            :title="`Insérer ${cat.insertText}`"
                          >
                            <span class="font-mono text-emerald-400/80 mr-0.5">{{ cat.insertText }}</span> {{ cat.displayLabel }}
                          </button>
                          <button
                            v-if="!cat.isSelf"
                            type="button"
                            class="text-[9px] px-1.5 py-0.5 rounded bg-muted border border-border hover:border-emerald-500 hover:text-emerald-400 text-muted-foreground/60 transition-colors font-bold"
                            @click="insertCategoryRef(cat.insertTextPrev)"
                            title="Insérer la valeur du mois précédent"
                          >
                            M-1
                          </button>
                        </div>
                      </div>
                    </div>
                    <div v-if="persoExpense.length > 0">
                      <p class="text-[9px] font-black uppercase tracking-wider text-red-400 mb-1.5">Dépenses</p>
                      <div class="flex flex-wrap gap-1">
                        <div v-for="(cat, idx) in persoExpense" :key="idx" class="flex items-center gap-0.5">
                          <button
                            type="button"
                            class="text-[10px] px-2 py-0.5 rounded bg-muted border border-border hover:border-red-400 hover:text-red-400 text-muted-foreground transition-colors text-left flex-1"
                            @click="insertCategoryRef(cat.insertText)"
                            :title="`Insérer ${cat.insertText}`"
                          >
                            <span class="font-mono text-red-400/80 mr-0.5">{{ cat.insertText }}</span> {{ cat.displayLabel }}
                          </button>
                          <button
                            v-if="!cat.isSelf"
                            type="button"
                            class="text-[9px] px-1.5 py-0.5 rounded bg-muted border border-border hover:border-red-400 hover:text-red-400 text-muted-foreground/60 transition-colors font-bold"
                            @click="insertCategoryRef(cat.insertTextPrev)"
                            title="Insérer la valeur du mois précédent"
                          >
                            M-1
                          </button>
                        </div>
                      </div>
                    </div>
                    <div v-if="persoParameter.length > 0">
                      <p class="text-[9px] font-black uppercase tracking-wider text-indigo-400 mb-1.5">Paramètres</p>
                      <div class="flex flex-wrap gap-1">
                        <div v-for="(cat, idx) in persoParameter" :key="idx" class="flex items-center gap-0.5">
                          <button
                            type="button"
                            class="text-[10px] px-2 py-0.5 rounded bg-muted border border-border hover:border-indigo-400 hover:text-indigo-400 text-muted-foreground transition-colors text-left flex-1"
                            @click="insertCategoryRef(cat.insertText)"
                            :title="`Insérer ${cat.insertText}`"
                          >
                            <span class="font-mono text-indigo-400/80 mr-0.5">{{ cat.insertText }}</span> {{ cat.displayLabel }}
                          </button>
                          <button
                            v-if="!cat.isSelf"
                            type="button"
                            class="text-[9px] px-1.5 py-0.5 rounded bg-muted border border-border hover:border-indigo-400 hover:text-indigo-400 text-muted-foreground/60 transition-colors font-bold"
                            @click="insertCategoryRef(cat.insertTextPrev)"
                            title="Insérer la valeur du mois précédent"
                          >
                            M-1
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Group 3: Données Annuelles -->
                <div class="border border-border rounded-xl overflow-hidden bg-muted/10">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-3.5 py-2 hover:bg-muted/40 text-xs font-bold text-foreground transition-colors"
                    @click="toggleGroup('annual')"
                  >
                    <span class="flex items-center gap-1.5">
                      <span>📊</span> Données Annuelles
                    </span>
                    <span class="text-[9px] text-muted-foreground transition-transform duration-200" :class="{ 'rotate-180': openGroups.annual }">▼</span>
                  </button>
                  <div v-show="openGroups.annual" class="p-3 bg-card border-t border-border">
                    <div class="grid grid-cols-2 gap-1.5">
                      <button
                        v-for="(cat, idx) in annualVariables"
                        :key="idx"
                        type="button"
                        class="text-[10px] p-2 rounded bg-muted border border-border hover:border-primary text-muted-foreground hover:text-foreground transition-colors text-left flex flex-col gap-0.5"
                        @click="insertCategoryRef(cat.insertText)"
                        :title="`Insérer ${cat.insertText}`"
                      >
                        <span class="font-mono text-primary font-bold">{{ cat.insertText }}</span>
                        <span class="text-[9px] text-muted-foreground/60 leading-tight">{{ cat.displayLabel }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Group 4: Variables Globales -->
                <div class="border border-border rounded-xl overflow-hidden bg-muted/10">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-3.5 py-2 hover:bg-muted/40 text-xs font-bold text-foreground transition-colors"
                    @click="toggleGroup('global')"
                  >
                    <span class="flex items-center gap-1.5">
                      <span>🌐</span> Variables Globales
                    </span>
                    <span class="text-[9px] text-muted-foreground transition-transform duration-200" :class="{ 'rotate-180': openGroups.global }">▼</span>
                  </button>
                  <div v-show="openGroups.global" class="p-3 bg-card border-t border-border">
                    <div class="grid grid-cols-2 gap-1.5">
                      <button
                        v-for="(cat, idx) in globalVariables"
                        :key="idx"
                        type="button"
                        class="text-[10px] p-2 rounded bg-muted border border-border hover:border-primary text-muted-foreground hover:text-foreground transition-colors text-left flex flex-col gap-0.5"
                        @click="insertCategoryRef(cat.insertText)"
                        :title="`Insérer ${cat.insertText}`"
                      >
                        <span class="font-mono text-primary font-bold">{{ cat.insertText }}</span>
                        <span class="text-[9px] text-muted-foreground/60 leading-tight">{{ cat.displayLabel }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sort order -->
          <div>
            <label for="cat-sort" class="block text-xs font-black uppercase tracking-wider text-muted-foreground mb-2">
              Ordre d'affichage
            </label>
            <input
              id="cat-sort"
              v-model.number="form.sortOrder"
              type="number"
              min="0"
              step="1"
              class="w-28 px-3 py-2 rounded-lg bg-muted border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition"
            />
          </div>

          <!-- Propriétés sous forme de Tags -->
          <div>
            <label class="block text-xs font-black uppercase tracking-wider text-muted-foreground mb-2">
              Propriétés
            </label>
            <div class="grid grid-cols-2 gap-2">
              
              <!-- Revenu Imposable (IR) -->
              <button
                v-if="form.type === 'income'"
                type="button"
                class="flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-xs transition-all text-center gap-1.5"
                :class="form.isTaxable ? 'border-primary bg-primary/10 text-primary font-bold shadow-sm' : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'"
                @click="form.isTaxable = !form.isTaxable"
                title="Prendre en compte dans le calcul de l'impôt estimé (IR)"
              >
                <CategoryPropertyIcon name="tax" class="w-5 h-5" />
                <span>Revenu Imposable</span>
              </button>

              <!-- Rémunération TNS -->
              <button
                v-if="form.accountType === 'professional' && form.type === 'expense'"
                type="button"
                class="flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-xs transition-all text-center gap-1.5"
                :class="form.isTnsRemuneration ? 'border-violet-500 bg-violet-500/10 text-violet-500 font-bold shadow-sm' : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'"
                @click="form.isTnsRemuneration = !form.isTnsRemuneration"
                title="Sert de base pour estimer les cotisations URSSAF (~45%)"
              >
                <CategoryPropertyIcon name="tns" class="w-5 h-5" />
                <span>Base URSSAF (TNS)</span>
              </button>

              <!-- Acompte URSSAF -->
              <button
                v-if="form.accountType === 'professional' && form.type === 'expense'"
                type="button"
                class="flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-xs transition-all text-center gap-1.5"
                :class="form.isUrssafPayment ? 'border-sky-500 bg-sky-500/10 text-sky-500 font-bold shadow-sm' : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'"
                @click="form.isUrssafPayment = !form.isUrssafPayment"
                title="Sera déduit de la provision URSSAF estimée"
              >
                <CategoryPropertyIcon name="urssaf" class="w-5 h-5" />
                <span>Acompte URSSAF</span>
              </button>

              <!-- Acompte Impôt (IR) -->
              <button
                v-if="form.accountType === 'personal' && form.type === 'expense'"
                type="button"
                class="flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-xs transition-all text-center gap-1.5"
                :class="form.isTaxPayment ? 'border-primary bg-primary/10 text-primary font-bold shadow-sm' : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'"
                @click="form.isTaxPayment = !form.isTaxPayment"
                title="Sera déduit de l'impôt sur le revenu estimé"
              >
                <CategoryPropertyIcon name="tax" class="w-5 h-5" />
                <span>Acompte Impôt (IR)</span>
              </button>

              <!-- Acompte IS -->
              <button
                v-if="form.accountType === 'professional' && form.type === 'expense'"
                type="button"
                class="flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-xs transition-all text-center gap-1.5"
                :class="form.isCorporateTaxPayment ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500 font-bold shadow-sm' : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'"
                @click="form.isCorporateTaxPayment = !form.isCorporateTaxPayment"
                title="Sera déduit de l'Impôt sur les Sociétés estimé"
              >
                <CategoryPropertyIcon name="is" colorClass="text-emerald-500" class="w-5 h-5" />
                <span>Acompte IS</span>
              </button>

              <!-- Revenu IS -->
              <button
                v-if="form.accountType === 'professional' && (form.type === 'income' || form.type === 'parameter')"
                type="button"
                class="flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-xs transition-all text-center gap-1.5"
                :class="form.corporateTaxDirection === 'income' ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500 font-bold shadow-sm' : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'"
                @click="form.corporateTaxDirection = form.corporateTaxDirection === 'income' ? null : 'income'"
                title="Augmente la base imposable pour l'Impôt sur les Sociétés"
              >
                <CategoryPropertyIcon name="is" colorClass="text-emerald-500" class="w-5 h-5" />
                <span>Revenu (IS)</span>
              </button>

              <!-- Charge IS -->
              <button
                v-if="form.accountType === 'professional' && (form.type === 'expense' || form.type === 'parameter')"
                type="button"
                class="flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-xs transition-all text-center gap-1.5"
                :class="form.corporateTaxDirection === 'expense' ? 'border-red-500 bg-red-500/10 text-red-500 font-bold shadow-sm' : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'"
                @click="form.corporateTaxDirection = form.corporateTaxDirection === 'expense' ? null : 'expense'"
                title="Diminue la base imposable pour l'Impôt sur les Sociétés"
              >
                <CategoryPropertyIcon name="is" colorClass="text-red-500" class="w-5 h-5" />
                <span>Charge (IS)</span>
              </button>

              <!-- Dette -->
              <button
                v-if="form.type === 'parameter'"
                type="button"
                class="flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-xs transition-all text-center gap-1.5"
                :class="form.isDebt ? 'border-orange-500 bg-orange-500/10 text-orange-500 font-bold shadow-sm' : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'"
                @click="form.isDebt = !form.isDebt"
                title="Son montant en décembre viendra en déduction du solde net annuel"
              >
                <CategoryPropertyIcon name="debt" class="w-5 h-5" />
                <span>Est une Dette</span>
              </button>

              <!-- Faire la Somme -->
              <button
                v-if="form.type === 'parameter'"
                type="button"
                class="flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-xs transition-all text-center gap-1.5"
                :class="form.isParameterSum ? 'border-indigo-500 bg-indigo-500/10 text-indigo-500 font-bold shadow-sm' : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'"
                @click="form.isParameterSum = !form.isParameterSum"
                title="Affiche le total annuel de ce paramètre dans la dernière colonne"
              >
                <CategoryPropertyIcon name="sum" class="w-5 h-5 text-indigo-500" />
                <span>Faire la Somme</span>
              </button>

              <!-- Non modifiable -->
              <button
                type="button"
                class="flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-xs transition-all text-center gap-1.5"
                :class="form.isNonEditable ? 'border-amber-500 bg-amber-500/10 text-amber-500 font-bold shadow-sm' : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'"
                @click="form.isNonEditable = !form.isNonEditable"
                title="Verrouille la catégorie : seule la formule définit sa valeur"
              >
                <CategoryPropertyIcon name="lock" class="w-5 h-5" />
                <span>Non modifiable</span>
              </button>

            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-1">
            <button
              type="button"
              class="flex-1 py-2.5 rounded-xl border border-border text-sm font-bold text-muted-foreground hover:bg-muted transition-colors"
              @click="emit('close')"
            >Annuler</button>
            <button
              type="submit"
              class="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!canSubmit || saving"
            >
              {{ saving ? 'Enregistrement…' : (isEditing ? 'Mettre à jour' : 'Créer') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import type { BudgetCategoryData } from '../../../composables/useBudget'
import { useBudget } from '../../../composables/useBudget'
import { useToast } from '../../../composables/useToast.js'
import CategoryPropertyIcon from './CategoryPropertyIcon.vue'

// ── Props / Emits ─────────────────────────────────────────────────────────────

const props = defineProps<{
  isOpen:              boolean
  editCategory?:       BudgetCategoryData | null
  prefilledAccountType?: 'personal' | 'professional' | null
  prefilledType?:       'income' | 'expense' | 'parameter' | null
  allCategories:       BudgetCategoryData[]
}>()

const emit = defineEmits<{
  close: []
  saved: [category: BudgetCategoryData]
}>()

// ── Composables ───────────────────────────────────────────────────────────────

const { createCategory, updateCategory, validateFormula } = useBudget()
const toast = useToast()

// ── State ─────────────────────────────────────────────────────────────────────

const form = ref({
  name:        '',
  accountType: (props.prefilledAccountType ?? 'professional') as 'personal' | 'professional',
  type:        (props.prefilledType ?? 'income') as 'income' | 'expense' | 'parameter',
  formula:     '',
  sortOrder:   0,
  isTaxable:   false,
  isTnsRemuneration: false,
  isUrssafPayment: false,
  isTaxPayment: false,
  isCorporateTaxPayment: false,
  isNonEditable: false,
  isDebt: false,
  isParameterSum: false,
  corporateTaxDirection: null as 'income' | 'expense' | null,
})

const formulaError  = ref<string | null>(null)
const formulaValid  = ref<boolean | null>(null)
const validating    = ref(false)
const saving        = ref(false)

let validationTimer: ReturnType<typeof setTimeout> | null = null

// ── Constants ─────────────────────────────────────────────────────────────────

const accountOptions = [
  { value: 'professional', label: 'Pro',   icon: '💼' },
  { value: 'personal',     label: 'Perso', icon: '🏠' },
]

const typeOptions = [
  { value: 'income',    label: '↑ Recette' },
  { value: 'expense',   label: '↓ Dépense' },
  { value: 'parameter', label: '⚙️ Paramètre' },
]

// ── Computed ──────────────────────────────────────────────────────────────────

const isEditing = computed(() => !!props.editCategory)

const canSubmit = computed(() => {
  if (!form.value.name.trim()) return false
  if (form.value.formula && formulaValid.value !== true && formulaError.value !== null) return false
  return true
})

const availableCategories = computed(() => {
  const cats = props.allCategories
    .filter(c => c.id !== props.editCategory?.id)
    .map(c => ({
      insertText: `{cat:${c.id}}`,
      insertTextPrev: `{cat:${c.id}:-1}`,
      displayLabel: c.name,
      accountType: c.accountType,
      type: c.type,
      id: c.id,
      isSelf: false,
    }))

  if (props.editCategory) {
    cats.unshift({
      insertText: '{cat:current:-1}',
      insertTextPrev: '{cat:current:-1}',
      displayLabel: `${props.editCategory.name} (mois précédent)`,
      accountType: props.editCategory.accountType,
      type: props.editCategory.type,
      id: props.editCategory.id,
      isSelf: true,
    })
  }

  return cats
})

const proCategories = computed(() => {
  return availableCategories.value.filter(c => c.accountType === 'professional')
})

const proIncome = computed(() => proCategories.value.filter(c => c.type === 'income'))
const proExpense = computed(() => proCategories.value.filter(c => c.type === 'expense'))
const proParameter = computed(() => proCategories.value.filter(c => c.type === 'parameter'))

const persoCategories = computed(() => {
  return availableCategories.value.filter(c => c.accountType === 'personal')
})

const persoIncome = computed(() => persoCategories.value.filter(c => c.type === 'income'))
const persoExpense = computed(() => persoCategories.value.filter(c => c.type === 'expense'))
const persoParameter = computed(() => persoCategories.value.filter(c => c.type === 'parameter'))

const annualVariables = computed(() => [
  { insertText: '{RESTE_URSSAF}', displayLabel: 'URSSAF restant (année en cours)' },
  { insertText: '{RESTE_URSSAF:-1}', displayLabel: 'URSSAF restant (N-1)' },
  { insertText: '{RESTE_IR}', displayLabel: 'Impôt restant (IR/perso, année en cours)' },
  { insertText: '{RESTE_IR:-1}', displayLabel: 'Impôt restant (IR/perso, N-1)' },
  { insertText: '{RESTE_IS}', displayLabel: 'IS restant (pro, année en cours)' },
  { insertText: '{RESTE_IS:-1}', displayLabel: 'IS restant (pro, N-1)' },
])

const globalVariables = computed(() => [
  { insertText: '{cat:current:-1}', displayLabel: 'Valeur du mois précédent (M-1)' },
  { insertText: '{MOIS}', displayLabel: 'Mois en cours (1-12)' }
])

const openGroups = ref({
  pro: false,
  perso: false,
  annual: false,
  global: false,
})

const showReferences = ref(false)
const showReadableFormula = ref(false)

function stripOuterParentheses(s: string): string {
  s = s.trim()
  while (s.startsWith('(') && s.endsWith(')')) {
    let level = 0
    let match = true
    for (let i = 0; i < s.length - 1; i++) {
      if (s[i] === '(') level++
      else if (s[i] === ')') level--
      if (level === 0 && i > 0) {
        match = false
        break
      }
    }
    if (match) {
      s = s.substring(1, s.length - 1).trim()
    } else {
      break
    }
  }
  return s
}

function parseTernary(expr: string): string {
  expr = expr.trim()
  
  let stripped = stripOuterParentheses(expr)
  if (stripped !== expr) {
    return `(${parseTernary(stripped)})`
  }

  let parenLevel = 0
  let braceLevel = 0
  let qIndex = -1
  let cIndex = -1

  for (let i = 0; i < expr.length; i++) {
    const char = expr[i]
    if (char === '(') parenLevel++
    else if (char === ')') parenLevel--
    else if (char === '{') braceLevel++
    else if (char === '}') braceLevel--
    else if (char === '?' && parenLevel === 0 && braceLevel === 0) {
      if (qIndex === -1) {
        qIndex = i
      }
    } else if (char === ':' && parenLevel === 0 && braceLevel === 0) {
      if (qIndex !== -1 && cIndex === -1) {
        let qCount = 0
        let tempParen = 0
        let tempBrace = 0
        for (let j = qIndex + 1; j < i; j++) {
          if (expr[j] === '(') tempParen++
          else if (expr[j] === ')') tempParen--
          else if (expr[j] === '{') tempBrace++
          else if (expr[j] === '}') tempBrace--
          else if (expr[j] === '?' && tempParen === 0 && tempBrace === 0) qCount++
        }
        let cCount = 0
        tempParen = 0
        tempBrace = 0
        for (let j = qIndex + 1; j < i; j++) {
          if (expr[j] === '(') tempParen++
          else if (expr[j] === ')') tempParen--
          else if (expr[j] === '{') tempBrace++
          else if (expr[j] === '}') tempBrace--
          else if (expr[j] === ':' && tempParen === 0 && tempBrace === 0) cCount++
        }
        if (qCount === cCount) {
          cIndex = i
          break
        }
      }
    }
  }

  if (qIndex !== -1 && cIndex !== -1) {
    const condition = expr.substring(0, qIndex).trim()
    const thenBranch = expr.substring(qIndex + 1, cIndex).trim()
    const elseBranch = expr.substring(cIndex + 1).trim()

    const indent = (text: string) => {
      return text.split('\n').map(line => '  ' + line).join('\n')
    }

    return `Si ${parseTernary(condition)} :\n${indent('Alors : ' + parseTernary(thenBranch))}\n${indent('Sinon : ' + parseTernary(elseBranch))}`
  }

  let result = ''
  let lastIndex = 0
  parenLevel = 0
  let groupStart = -1

  for (let i = 0; i < expr.length; i++) {
    const char = expr[i]
    if (char === '(') {
      if (parenLevel === 0) {
        groupStart = i
      }
      parenLevel++
    } else if (char === ')') {
      parenLevel--
      if (parenLevel === 0 && groupStart !== -1) {
        result += expr.substring(lastIndex, groupStart)
        const inner = expr.substring(groupStart + 1, i)
        result += `(${parseTernary(inner)})`
        lastIndex = i + 1
      }
    }
  }
  result += expr.substring(lastIndex)
  return result
}

const readableFormula = computed(() => {
  if (!form.value.formula) return ''
  let expr = form.value.formula

  // Replace {cat:current:-1} with '{Valeur précédente}'
  expr = expr.replace(/\{cat:current:\s*-1\}/gi, '{Valeur précédente}')
  // Replace {cat:current:-N} with '{Cette catégorie [M-N]}'
  expr = expr.replace(/\{cat:current:\s*-(\d+)\}/gi, (_, offsetStr) => `{Cette catégorie [M-${offsetStr}]}`)
  // Replace {cat:current} with '{Cette catégorie}'
  expr = expr.replace(/\{cat:current\}/gi, '{Cette catégorie}')

  // Replace {cat:ID} or {cat:ID:-N}
  expr = expr.replace(/\{cat:(\d+)(?::\s*-(\d+))?\}/gi, (_, idStr, offsetStr) => {
    const id = parseInt(idStr, 10)
    const cat = props.allCategories.find(c => c.id === id)
    const label = cat ? cat.name : `Catégorie #${id}`
    const suffix = offsetStr ? ` [M-${offsetStr}]` : ''
    return `{${label}${suffix}}`
  })

  // Replace {RESTE_URSSAF} or {RESTE_URSSAF:-N}
  expr = expr.replace(/\{RESTE_URSSAF(?::\s*-(\d+))?\}/gi, (_, offsetStr) => {
    const suffix = offsetStr ? ` [N-${offsetStr}]` : ''
    return `{URSSAF Restant${suffix}}`
  })

  // Replace {RESTE_IR} or {RESTE_IR:-N}
  expr = expr.replace(/\{RESTE_IR(?::\s*-(\d+))?\}/gi, (_, offsetStr) => {
    const suffix = offsetStr ? ` [N-${offsetStr}]` : ''
    return `{Impôt Restant${suffix}}`
  })

  // Replace {RESTE_IS} or {RESTE_IS:-N}
  expr = expr.replace(/\{RESTE_IS(?::\s*-(\d+))?\}/gi, (_, offsetStr) => {
    const suffix = offsetStr ? ` [N-${offsetStr}]` : ''
    return `{IS Restant${suffix}}`
  })

  // Replace {MOIS}
  expr = expr.replace(/\{MOIS\}/gi, '{Mois en cours}')

  return parseTernary(expr)
})

function toggleGroup(key: 'pro' | 'perso' | 'annual' | 'global') {
  openGroups.value[key] = !openGroups.value[key]
}

const formulaInputRef = ref<HTMLTextAreaElement | null>(null)

function computeNextSortOrder(accountType: string, type: string): number {
  const matchingCats = props.allCategories.filter(c => c.accountType === accountType && c.type === type)
  if (matchingCats.length === 0) return 1
  return Math.max(...matchingCats.map(c => c.sortOrder)) + 1
}

// ── Watchers ──────────────────────────────────────────────────────────────────

watch(() => props.isOpen, (open) => {
  if (open) {
    if (props.editCategory) {
      form.value = {
        name:        props.editCategory.name,
        accountType: props.editCategory.accountType,
        type:        props.editCategory.type,
        formula:     props.editCategory.formula ?? '',
        sortOrder:   props.editCategory.sortOrder,
        isTaxable:   props.editCategory.isTaxable ?? false,
        isTnsRemuneration: props.editCategory.isTnsRemuneration ?? false,
        isUrssafPayment: props.editCategory.isUrssafPayment ?? false,
        isTaxPayment: props.editCategory.isTaxPayment ?? false,
        isCorporateTaxPayment: props.editCategory.isCorporateTaxPayment ?? false,
        isNonEditable: props.editCategory.isNonEditable ?? false,
        isDebt:      props.editCategory.isDebt ?? false,
        isParameterSum: props.editCategory.isParameterSum ?? false,
        corporateTaxDirection: props.editCategory.corporateTaxDirection ?? null,
      }
    } else {
      const initAccount = props.prefilledAccountType ?? 'professional'
      const initType    = props.prefilledType ?? 'income'
      form.value = {
        name:        '',
        accountType: initAccount,
        type:        initType,
        formula:     '',
        sortOrder:   computeNextSortOrder(initAccount, initType),
        isTaxable:   false,
        isTnsRemuneration: false,
        isUrssafPayment: false,
        isTaxPayment: false,
        isCorporateTaxPayment: false,
        isNonEditable: false,
        isDebt:      false,
        isParameterSum: false,
        corporateTaxDirection: null,
      }
    }
    formulaError.value = null
    formulaValid.value = null

    // All collapses closed by default
    openGroups.value = {
      pro: false,
      perso: false,
      annual: false,
      global: false,
    }
    showReferences.value = false
    showReadableFormula.value = !!form.value.formula
  }
})

watch([() => form.value.accountType, () => form.value.type], ([newAccount, newType]) => {
  if (!isEditing.value && props.isOpen) {
    form.value.sortOrder = computeNextSortOrder(newAccount, newType)
  }
  if (newType !== 'parameter') {
    form.value.isDebt = false
    form.value.isParameterSum = false
  }
  if (newAccount !== 'professional') {
    form.value.corporateTaxDirection = null
  }
})

// ── Formula validation (debounced) ────────────────────────────────────────────

function onFormulaInput(): void {
  formulaError.value = null
  formulaValid.value = null

  if (validationTimer) clearTimeout(validationTimer)
  if (!form.value.formula) return

  validating.value = true
  validationTimer = setTimeout(async () => {
    try {
      const result = await validateFormula(form.value.formula, props.editCategory?.id ?? 0)
      formulaValid.value = result.valid
      formulaError.value = result.error
    } catch {
      formulaError.value = 'Erreur de validation'
    } finally {
      validating.value = false
    }
  }, 600)
}

function insertCategoryRef(insertText: string): void {
  showReadableFormula.value = false

  nextTick(() => {
    const textarea = formulaInputRef.value
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = form.value.formula || ''
      form.value.formula = text.substring(0, start) + insertText + text.substring(end)

      const newCursorPos = start + insertText.length
      textarea.focus()
      textarea.setSelectionRange(newCursorPos, newCursorPos)
      onFormulaInput()
    } else {
      form.value.formula = (form.value.formula || '') + insertText
      onFormulaInput()
    }
  })
}

// ── Submit ────────────────────────────────────────────────────────────────────

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) return
  saving.value = true

  try {
    const payload = {
      name:        form.value.name.trim(),
      accountType: form.value.accountType,
      type:        form.value.type,
      formula:     form.value.formula || null,
      sortOrder:   form.value.sortOrder,
      isTaxable:   form.value.isTaxable,
      isTnsRemuneration: form.value.isTnsRemuneration,
      isUrssafPayment: form.value.isUrssafPayment,
      isTaxPayment: form.value.isTaxPayment,
      isCorporateTaxPayment: form.value.isCorporateTaxPayment,
      isNonEditable: form.value.isNonEditable,
      isDebt:      form.value.isDebt,
      isParameterSum: form.value.isParameterSum,
      corporateTaxDirection: form.value.accountType === 'professional' ? form.value.corporateTaxDirection : null,
    }

    if (isEditing.value && props.editCategory) {
      await updateCategory(props.editCategory.id, payload)
      toast.success('Catégorie mise à jour')
      emit('saved', { ...props.editCategory, ...payload })
    } else {
      const created = await createCategory(payload)
      toast.success('Catégorie créée')
      emit('saved', created)
    }

    emit('close')
  } catch {
    toast.error('Erreur lors de la sauvegarde')
  } finally {
    saving.value = false
  }
}
</script>
